/**
 * "Makkelijk tikken": lang drukken en een trillende vinger tellen als tik.
 *
 * ── WAAROM ──────────────────────────────────────────────────────────────────
 *
 * Elke knop in de app reageert op `click`, en die komt niet van de vinger maar van de
 * gebarenherkenning van de WebView. Die is streng:
 *   • langer dan ~0,5 s ingedrukt  → "long press", er komt GEEN click;
 *   • meer dan een paar mm bewogen → "scroll", `pointercancel` en GEEN click — ook als
 *     er niets te scrollen valt.
 * Voor bewoners die rustig en stevig drukken, of met een trillende hand, doet een knop
 * dan gewoon niets (meldingen oktober 2026).
 *
 * ── HOE ─────────────────────────────────────────────────────────────────────
 *
 * We beslissen zelf wat een tik is, op `touchend` — dat komt ook door nadat de WebView
 * het gebaar al als scroll of long press heeft afgeschreven. Een tik is: één vinger,
 * neergezet op een element en losgelaten boven datzelfde element (met marge), niet te
 * ver weggegleden, en de inhoud is niet echt meegescrold. Dan annuleren we de eigen
 * click van de WebView (`preventDefault` op touchend) en sturen we er zelf één. Een
 * vangnet onderschept een native click die toch nog binnenkomt, zodat niets dubbel
 * afgaat.
 *
 * Velden, schuifregelaars en canvassen houden het native gedrag. Een onderdeel dat zelf
 * gebaren afhandelt en geen hulp wil, zet `data-native-tap` op zijn wortel.
 *
 * Aan/uit via de weergave-instelling `easyTap` (portaal → weergave per appartement).
 */

import { logEvent } from './debugLog';

/** Hoe ver de vinger tijdens de druk van het startpunt mag wegglijden (~7 mm). */
const MAX_TRAVEL_PX = 48;
/** Losgelaten tot zoveel buiten de rand van het element telt nog als erop. */
const EDGE_SLACK_PX = 24;
/** Is de inhoud méér dan dit meegescrold, dan was het scrollen, geen tik. */
const MAX_SCROLL_PX = 24;
/** Langer ingedrukt is een rustende hand, geen tik. */
const MAX_HOLD_MS = 5000;
/** Zo lang na onze eigen click vangen we een native click op hetzelfde gebaar af. */
const SUPPRESS_NATIVE_MS = 800;
/** Wat de WebView zelf nog een tik zou noemen; daarboven heeft deze laag "gered". */
const NATIVE_HOLD_MS = 500;
const NATIVE_SLOP_PX = 10;

/** Het element waarvan de rand telt: de dichtstbijzijnde knop of link, anders het doel zelf. */
const INTERACTIVE =
  'button, a[href], summary, [role="button"], [role="link"], [role="tab"], [role="menuitem"], '
  + '[role="option"], [role="checkbox"], [role="radio"], [role="switch"], [data-tap]';
/** Hier blijft de WebView de baas: tekstinvoer, keuzelijsten, schuifregelaars, tekenvlakken. */
const NATIVE_ONLY =
  'input, textarea, select, label, canvas, video, [contenteditable="true"], [data-native-tap]';

interface Press {
  id: number;
  target: Element;
  root: Element;
  x: number;
  y: number;
  t0: number;
  travel: number;
  rect0: DOMRect;
}

let installed = false;
let press: Press | null = null;
let suppressUntil = 0;

function tapRoot(el: Element): Element {
  return el.closest(INTERACTIVE) ?? el;
}

function isDisabled(el: Element): boolean {
  return (el as HTMLButtonElement).disabled === true
    || el.getAttribute('aria-disabled') === 'true';
}

function onTouchStart(e: TouchEvent) {
  press = null;
  if (e.touches.length !== 1) return; // knijpen of een tweede vinger: geen tik
  const target = e.target instanceof Element ? e.target : null;
  if (!target || target.closest(NATIVE_ONLY)) return;
  const touch = e.touches[0];
  const root = tapRoot(target);
  press = {
    id: touch.identifier,
    target,
    root,
    x: touch.clientX,
    y: touch.clientY,
    t0: Date.now(),
    travel: 0,
    rect0: root.getBoundingClientRect(),
  };
}

function onTouchMove(e: TouchEvent) {
  if (!press) return;
  if (e.touches.length > 1) {
    press = null;
    return;
  }
  for (const touch of Array.from(e.changedTouches)) {
    if (touch.identifier !== press.id) continue;
    press.travel = Math.max(press.travel, Math.hypot(touch.clientX - press.x, touch.clientY - press.y));
  }
}

function onTouchEnd(e: TouchEvent) {
  const p = press;
  press = null;
  if (!p || e.touches.length > 0) return;
  const touch = Array.from(e.changedTouches).find((t) => t.identifier === p.id);
  if (!touch) return;

  const held = Date.now() - p.t0;
  const travel = Math.max(p.travel, Math.hypot(touch.clientX - p.x, touch.clientY - p.y));
  if (held > MAX_HOLD_MS || travel > MAX_TRAVEL_PX) return;
  if (!p.target.isConnected || !p.root.isConnected || isDisabled(p.root)) return;

  const rect = p.root.getBoundingClientRect();
  // De knop zelf is verschoven: de lijst scrolde of een carrousel schoof mee.
  if (Math.abs(rect.top - p.rect0.top) > MAX_SCROLL_PX || Math.abs(rect.left - p.rect0.left) > MAX_SCROLL_PX) return;
  const onIt = touch.clientX >= rect.left - EDGE_SLACK_PX && touch.clientX <= rect.right + EDGE_SLACK_PX
    && touch.clientY >= rect.top - EDGE_SLACK_PX && touch.clientY <= rect.bottom + EDGE_SLACK_PX;
  if (!onIt) return;

  // Geen native click meer laten komen; wij sturen er precies één.
  if (e.cancelable) e.preventDefault();
  suppressUntil = Date.now() + SUPPRESS_NATIVE_MS;

  // Een echte tik geeft de knop focus (en haalt die zo weg uit een open invoerveld).
  if (p.root instanceof HTMLElement && p.root !== document.activeElement && p.root.matches(INTERACTIVE)) {
    try { p.root.focus({ preventScroll: true }); } catch { /* niet focusbaar */ }
  }

  p.target.dispatchEvent(new MouseEvent('click', {
    bubbles: true,
    cancelable: true,
    composed: true,
    detail: 1,
    clientX: touch.clientX,
    clientY: touch.clientY,
    screenX: touch.screenX,
    screenY: touch.screenY,
  }));

  // Alleen loggen wat de WebView zelf had laten vallen — dat is wat de uitrol moet tonen.
  if (held > NATIVE_HOLD_MS || travel > NATIVE_SLOP_PX) {
    logEvent('easyTap', { rescued: true, heldMs: held, travelPx: Math.round(travel) });
  }
}

function onTouchCancel() {
  press = null;
}

/** Vangnet: een native click die na onze eigen click toch nog binnenkomt. */
function onNativeClick(e: MouseEvent) {
  if (!e.isTrusted || Date.now() > suppressUntil) return;
  suppressUntil = 0;
  e.preventDefault();
  e.stopImmediatePropagation();
}

/**
 * Lang drukken met de vinger opent geen systeemmenu. Dat menu komt terwijl de vinger nog
 * ligt, dus terwijl `press` loopt; een rechtsklik met de muis heeft geen `press` en blijft
 * native. Alleen de standaardactie gaat weg: een eigen `onContextMenu` (de dimmers) draait.
 */
function onContextMenu(e: Event) {
  if (press) e.preventDefault();
}

/** Zet de laag aan of uit. Meermaals aanroepen met dezelfde waarde doet niets. */
export function setEasyTapEnabled(on: boolean): void {
  if (typeof document === 'undefined' || on === installed) return;
  installed = on;
  press = null;
  suppressUntil = 0;
  if (on) {
    document.addEventListener('touchstart', onTouchStart, { capture: true, passive: true });
    document.addEventListener('touchmove', onTouchMove, { capture: true, passive: true });
    // Niet passief: preventDefault hierop is wat de native click tegenhoudt.
    document.addEventListener('touchend', onTouchEnd, { capture: true, passive: false });
    document.addEventListener('touchcancel', onTouchCancel, { capture: true, passive: true });
    window.addEventListener('click', onNativeClick, { capture: true });
    document.addEventListener('contextmenu', onContextMenu, { capture: true });
  } else {
    document.removeEventListener('touchstart', onTouchStart, { capture: true });
    document.removeEventListener('touchmove', onTouchMove, { capture: true });
    document.removeEventListener('touchend', onTouchEnd, { capture: true });
    document.removeEventListener('touchcancel', onTouchCancel, { capture: true });
    window.removeEventListener('click', onNativeClick, { capture: true });
    document.removeEventListener('contextmenu', onContextMenu, { capture: true });
  }
}
